/**
 * ValidationModal Component
 * Modal untuk menampilkan warning/error saat validasi order
 */

import {
  Box,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
} from '@chakra-ui/react';
import { Button, Text } from '@diamond/shared/ui';
import type { WarningType } from '@diamond/sol-buyer/utils';
import {
  canContinueOrder,
  getContinueButtonText,
  getFooterWarningMessage,
  getReturnButtonVariant,
  getWarningModalSize,
  getWarningModalTitle,
  WARNING_TYPES,
} from '@diamond/sol-buyer/utils';

import { InvalidPromosSection } from './InvalidPromosSection';
import { ProductChangeItem } from './ProductChangeItem';
import type { ValidationModalProps } from './types';

/**
 * Get modal title with custom message override for price_stock_changes
 */
function getModalTitle(
  warningType: WarningType,
  warningMessage: string | null
): string {
  if (warningType === WARNING_TYPES.PRICE_STOCK_CHANGES && warningMessage) {
    return warningMessage;
  }
  return getWarningModalTitle(warningType);
}

/**
 * ValidationModal Component
 * Menampilkan modal validation dengan berbagai warning types
 */
export function ValidationModal({
  isOpen,
  onClose,
  warningType,
  warningMessage,
  productChanges,
  invalidPromos,
  invalidVoucher,
  onReturnToCart,
  onContinueOrder,
  isLoading = false,
}: ValidationModalProps) {
  const showContinueButton = canContinueOrder(warningType);
  const hasProductChanges = productChanges && productChanges.length > 0;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size={getWarningModalSize(warningType)}
      closeOnOverlayClick={false}
      isCentered
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader
          textAlign="center"
          fontSize={{ base: 'lg', md: 'xl' }}
          {...(warningType === 'no_product' && { paddingBottom: 0 })}
        >
          {getModalTitle(warningType, warningMessage)}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody paddingBottom={0}>
          <Stack spacing={4}>
            {/* Warning Message for non-price/stock validation types */}
            {warningType !== 'price_stock_changes' && warningMessage && (
              <Text
                align="center"
                fontSize={{ base: 'sm', md: 'md' }}
                color="gray.700"
                mb={2}
              >
                {warningMessage}
              </Text>
            )}

            {/* Out of Stock Products Label */}
            {warningType === 'min_order' && hasProductChanges && (
              <Text
                fontSize={{ base: 'sm', md: 'md' }}
                fontWeight="semibold"
                color="gray.700"
                mb={2}
              >
                Barang yang stoknya kosong:
              </Text>
            )}

            {/* Product Changes List */}
            {hasProductChanges && (
              <Box
                mb={4}
                overflow="visible"
                maxH={{ base: '400px', md: '500px' }}
                overflowY="auto"
              >
                <Stack spacing={3}>
                  {productChanges.map((item, idx) => (
                    <ProductChangeItem
                      key={`product-change-${idx}`}
                      item={item}
                      index={idx}
                      warningType={warningType}
                    />
                  ))}
                </Stack>
              </Box>
            )}

            {/* Invalid Promos Section */}
            {warningType === 'promo_invalid' && (
              <Box>
                <InvalidPromosSection invalidPromos={invalidPromos} />
              </Box>
            )}

            {/* Invalid Voucher Section */}
            {warningType === 'voucher_invalid' && invalidVoucher && (
              <Box textAlign="center">
                <Text
                  fontSize={{ base: 'sm', md: 'md' }}
                  color="gray.600"
                  mb={4}
                >
                  Voucher <strong>{invalidVoucher.code}</strong> sudah tidak
                  berlaku atau telah mencapai batas penggunaan.
                </Text>
              </Box>
            )}
          </Stack>
        </ModalBody>
        <ModalFooter>
          <Stack spacing={3} w="full">
            {/* Warning Message */}
            {warningType !== 'promo_invalid' &&
              warningType !== 'voucher_invalid' && (
                <Text
                  align="center"
                  fontSize={{ base: 'sm', md: 'md' }}
                  color="gray.600"
                  mb={2}
                >
                  {getFooterWarningMessage(warningType)}
                </Text>
              )}

            {/* Action Buttons */}
            <Flex
              direction={{ base: 'column', md: 'row' }}
              gap={3}
              justify="center"
              w="full"
            >
              {/* Return to Cart Button */}
              <Button
                variant={getReturnButtonVariant(warningType)}
                colorScheme="blue"
                onClick={onReturnToCart}
                w={{ base: 'full', md: 'auto' }}
                minW={{ md: '200px' }}
              >
                Kembali ke Keranjang
              </Button>

              {/* Continue Order Button */}
              {showContinueButton && (
                <Button
                  variant="solid"
                  colorScheme="blue"
                  onClick={onContinueOrder}
                  isLoading={isLoading}
                  loadingText="Memproses..."
                  w={{ base: 'full', md: 'auto' }}
                  minW={{ md: '200px' }}
                >
                  {getContinueButtonText(warningType)}
                </Button>
              )}
            </Flex>
          </Stack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
